import {createAction, createReducer} from 'redux-act';
import {FULFILLED, REJECTED, PENDING} from 'redux-promise-middleware';
import {or, pathOr, not, equals, and} from 'ramda';
import {AxiosPromise} from 'axios';
import {MID} from '@optimaxdev/wizard-ms';

import {CUSTOMER} from 'constants/actions';
import {FILL_IT, SEND_LATER, UPLOAD} from 'constants/wizard/prescriptionType';
import {fetchQuoteItemV2} from 'actions/cart/cart';
import {OAuthPrescriptionsType} from 'reducers/oAuth/oAuthType';
import {ENDPOINTS} from 'constants/endpoints';
import {API_URL} from 'constants/api';
import {instanceGusaBackend} from 'libs/api';
import {setSavedPrescription} from 'features/wizardMs/actions/prescription/prescription';

import {
    PrescriptionMethodType,
    OculusPrescriptionType,
    PdType,
    PrescriptionActionDataType,
    PrescriptionType,
} from './prescriptionTypes';
import {initialState, NONE} from './constants';

/**
 * List of actions
 */
export const changePrescriptionMethod = createAction<PrescriptionMethodType>(
    'WIZARD_CHANGE_PRESCRIPTION_METHOD',
);
export const setPrescription = createAction<
    PrescriptionActionDataType,
    {name: string; value: PdType | OculusPrescriptionType}
>(
    'SET_PRESCRIPTION',
    payload => payload,
    (payload, meta) => meta,
);
export const selectPrescription = createAction<PrescriptionType>('SELECT_PRESCRIPTION');

export const validatePrescription = createAction('VALIDATE_PRESCRIPTION');

export const updateCustomerPrescription = createAction<OAuthPrescriptionsType, AxiosPromise>(
    'UPDATE_CUSTOMER_PRESCRIPTION',
    (prescription: OAuthPrescriptionsType) =>
        instanceGusaBackend({
            method: 'put',
            baseURL: API_URL,
            url: `${ENDPOINTS.CUSTOMER_PRESCRIPTIONS}/${prescription.id}`,
            data: prescription,
        }),
);

export const prescription = createReducer({}, initialState)
    .on(setSavedPrescription, (state, payload) => ({...state, ...payload}))
    /**
     * Subscribe actions
     * https://www.npmjs.com/package/redux-act#typescript
     */
    .on(
        `${setPrescription}`,
        (state, {name, value}: {name: string; value: PdType | OculusPrescriptionType}) => {
            const isOdOrOs = name === 'os' || name === 'od';

            return {
                ...state,
                [name]: isOdOrOs
                    ? {
                          ...value,
                          axi: MID.includes((value as OculusPrescriptionType).cyl)
                              ? NONE
                              : (value as OculusPrescriptionType).axi,
                      }
                    : value,
            };
        },
    )
    .on(`${fetchQuoteItemV2}_${FULFILLED}`, (state, {data}) => {
        const method = pathOr<PrescriptionMethodType>(0, ['prescription', 'method'], data);

        if (method === FILL_IT) {
            return {
                id: data.prescription.id,
                name: data.prescription.name,
                method: data.prescription.method,
                comment: data.prescription.comment,
                file: state.file,
                od: data.prescription.od,
                os: data.prescription.os,
                pd: {
                    singular: data.prescription.pd.singular,
                    right: equals(data.prescription.pd.right, NONE)
                        ? initialState.pd.right
                        : data.prescription.pd.right,
                    left: equals(data.prescription.pd.left, NONE)
                        ? initialState.pd.left
                        : data.prescription.pd.left,
                    near: data.prescription.pd.near,
                },
                isMultiPD: or(
                    not(equals(data.prescription.pd.right, NONE)),
                    not(equals(data.prescription.pd.left, NONE)),
                ),
                status: 'fulfilled',
            };
        }

        if (method === UPLOAD) {
            return {
                ...state,
                method: data.prescription.method,
                file: {
                    ...state.file,
                    fileSrc: data.prescription.link,
                },
            };
        }

        if (method === SEND_LATER) {
            return {
                ...initialState,
                method: data.prescription.method,
            };
        }

        return initialState;
    })
    .on(`${CUSTOMER.FETCH_PRESCRIPTION}_${FULFILLED}`, (state, {data}) => {
        return {
            ...data,
            file: {
                ...initialState.file,
            },
            pd: {
                singular: data.pd.singular,
                right: data.pd.right,
                left: data.pd.left,
                near: data.pd.near,
            },
            isMultiPD: Boolean(
                and(data.pd.singular === NONE, or(data.pd.right !== NONE, data.pd.left !== NONE)),
            ),
            status: 'fulfilled',
        };
    })
    .on(`${CUSTOMER.FETCH_PRESCRIPTION}_${REJECTED}`, state => ({
        ...state,
        status: 'rejected',
    }))
    .on(`${CUSTOMER.FETCH_PRESCRIPTION}_${PENDING}`, state => ({
        ...state,
        status: 'pending',
    }))
    .on(changePrescriptionMethod, (state, payload) => ({
        ...state,
        method: payload,
    }))
    .on(selectPrescription, (state, payload) => {
        return {...state, ...payload};
    });
